import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import Tooltip from '../ui/Tooltip'
import Text from '../ui/Text'
import QuestionGradeContent from './QuestionGradeContent'
import QuestionSelectionContent from './QuestionSelectionContent'
import { SELECTION_MULTI_TOOLTIP, SELECTION_TOOLTIP, VIEW_MODES } from './constants'

const CONTEXT_LABEL = {
  grade: 'Question avec notation',
  selection: 'Question avec choix unique',
  // eslint-disable-next-line camelcase
  selection_multi: 'Question avec choix multiples'
}

const CONTEXT_TOOLTIP = {
  grade: null,
  selection: SELECTION_TOOLTIP,
  // eslint-disable-next-line camelcase
  selection_multi: SELECTION_MULTI_TOOLTIP
}

const subtitleParts = question => {
  const label = CONTEXT_LABEL[question.context] || ''
  const count = question.responses_count || 0
  const noun = count > 1 ? 'réponses' : 'réponse'
  const suffix = question.context === 'selection_multi' ? ' (plusieurs réponses possibles)' : ''
  return { label, count: `${count} ${noun}${suffix}` }
}

const SUBTITLE_DOT_CLASS = 'tw:bg-gray-400 tw:rounded-full tw:w-1 tw:h-1 tw:shrink-0'

const HEADER_BASE_CLASS = 'tw:group tw:flex tw:items-center tw:justify-between tw:w-full '
  + 'tw:p-4 tw:border-0 tw:cursor-pointer tw:text-left tw:gap-3'

const HEADER_CLASS_OPEN = `${HEADER_BASE_CLASS} tw:bg-gray-100 tw:rounded-t-sm`

const HEADER_CLASS_CLOSED = `${HEADER_BASE_CLASS} tw:bg-white tw:rounded-sm`

const BODY_CLASS = 'tw:p-4 tw:bg-white tw:border-0 tw:rounded-b-sm'

const TITLE_CLASS = 'tw:font-medium tw:text-gray-900 tw:text-base tw:leading-6 '
  + 'tw:break-all tw:text-left tw:min-w-0'

const SECTION_CLASS = 'tw:border tw:border-solid tw:border-gray-300 '
  + 'tw:rounded-sm! tw:bg-white tw:overflow-hidden'

const renderContent = (question, viewMode, gradeMax) => {
  if (question.context === 'grade')
    return <QuestionGradeContent gradeMax={gradeMax} question={question} viewMode={viewMode} />
  return <QuestionSelectionContent question={question} />
}

const QuestionItem = ({ question, isOpen, onToggle, viewMode, gradeMax }) => {
  const tooltip = CONTEXT_TOOLTIP[question.context]
  const { label: subtitleLabel, count: subtitleCount } = subtitleParts(question)
  const handleClick = useCallback(() => onToggle(question.id), [onToggle, question.id])
  const bodyId = `question-body-${question.id}`

  return (
    <section className={SECTION_CLASS}>
      <button
        aria-controls={bodyId}
        aria-expanded={isOpen}
        className={isOpen ? HEADER_CLASS_OPEN : HEADER_CLASS_CLOSED}
        type="button"
        onClick={handleClick}
      >
        <div className="tw:flex tw:flex-col tw:flex-1 tw:min-w-0 tw:gap-2">
          <div className="tw:flex tw:items-start tw:gap-2 tw:min-w-0">
            <Text className={TITLE_CLASS} lines={2}>{question.title}</Text>
            {tooltip ? (
              <Tooltip content={tooltip}>
                <i
                  aria-hidden="true"
                  className={'fas fa-question-circle tw:text-gray-600 '
                    + 'tw:hover:text-gray-800 tw:text-sm tw:pt-[5px] tw:shrink-0'}
                />
              </Tooltip>
            ) : null}
          </div>
          <span className="tw:flex tw:items-center tw:gap-1 tw:text-sm tw:text-gray-600">
            <span>{subtitleLabel}</span>
            <span aria-hidden="true" className={SUBTITLE_DOT_CLASS} />
            <span>{subtitleCount}</span>
          </span>
        </div>
        <i
          aria-hidden="true"
          className={`fas ${isOpen ? 'fa-chevron-up' : 'fa-chevron-down'} `
            + 'tw:text-gray-800 tw:group-hover:text-gray-900 tw:text-xs'}
        />
      </button>
      {isOpen ? (
        <div className={BODY_CLASS} id={bodyId} role="region">
          {renderContent(question, viewMode, gradeMax)}
        </div>
      ) : null}
    </section>
  )
}

QuestionItem.propTypes = {
  gradeMax: PropTypes.number.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  question: PropTypes.shape({
    context: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired,
    // eslint-disable-next-line camelcase
    responses_count: PropTypes.number,
    title: PropTypes.string.isRequired
  }).isRequired,
  viewMode: PropTypes.oneOf(Object.values(VIEW_MODES)).isRequired
}

export default QuestionItem
